import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { FileText, ArrowRight, CheckCircle, Clock } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { performAIAnalysis } from '../../lib/ai-analysis'
import toast from 'react-hot-toast'

interface TextAssessmentProps {
  employeeId: string
  onComplete: (results: any) => void
  questions?: string[]
}

const defaultQuestions = [
  "How would you describe your overall mood and energy levels this week?",
  "What aspects of your work are you finding most engaging or fulfilling right now?",
  "Are there any challenges or stressors you're currently facing at work?",
  "How would you rate your work-life balance, and what could be improved?",
  "What support or resources would be most helpful for your wellbeing right now?"
]

export const TextAssessment: React.FC<TextAssessmentProps> = ({
  employeeId,
  onComplete,
  questions = defaultQuestions
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [responses, setResponses] = useState<string[]>([])
  const [currentResponse, setCurrentResponse] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [assessmentComplete, setAssessmentComplete] = useState(false)
  const [results, setResults] = useState<any>(null)

  const currentQuestion = questions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === questions.length - 1

  const handleNext = async () => {
    if (!currentResponse.trim()) {
      toast.error('Please provide a response before continuing')
      return
    }

    const newResponses = [...responses, currentResponse.trim()]
    setResponses(newResponses)
    setCurrentResponse('')

    if (isLastQuestion) {
      await completeAssessment(newResponses)
    } else {
      setCurrentQuestionIndex(prev => prev + 1)
    }
  }

  const handleSkip = () => {
    const newResponses = [...responses, '']
    setResponses(newResponses)
    setCurrentResponse('')

    if (isLastQuestion) {
      completeAssessment(newResponses)
    } else {
      setCurrentQuestionIndex(prev => prev + 1)
    }
  }

  const completeAssessment = async (allResponses: string[]) => {
    setIsProcessing(true)
    
    try {
      // Combine all responses for analysis
      const combinedText = allResponses
        .filter(response => response.trim())
        .join(' ')

      if (!combinedText.trim()) {
        toast.error('Please provide at least one response to complete the assessment')
        setIsProcessing(false)
        return
      }

      // Perform AI analysis
      const aiAnalysis = await performAIAnalysis({
        text: combinedText,
        employee_context: {
          id: employeeId,
          department: 'Engineering', // Would come from employee data
          role: 'Developer',
          tenure_months: 12,
          previous_assessments: 0
        },
        analysis_type: 'comprehensive'
      })

      const assessmentResults = {
        employee_id: employeeId,
        assessment_type: 'text',
        responses: questions.map((question, index) => ({
          question,
          response: allResponses[index] || '',
          question_index: index
        })),
        overall_metrics: {
          sentiment_score: aiAnalysis.results.sentiment_score,
          stress_level: aiAnalysis.results.stress_level,
          engagement_level: aiAnalysis.results.engagement_level,
          burnout_risk: aiAnalysis.results.burnout_risk
        },
        ai_insights: aiAnalysis,
        recommendations: aiAnalysis.results.recommendations,
        completed_at: new Date().toISOString()
      }

      setResults(assessmentResults)
      setAssessmentComplete(true)
      onComplete(assessmentResults)
      
      toast.success('Text assessment completed successfully!')
    } catch (error) {
      console.error('Error completing assessment:', error)
      toast.error('Failed to complete assessment')
    } finally {
      setIsProcessing(false)
    }
  }

  if (assessmentComplete && results) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <Card className="p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Assessment Complete!
          </h2>
          
          <p className="text-slate-600 mb-6">
            Thank you for taking the time to share your thoughts. Your responses help us better understand and support your wellbeing.
          </p>

          {/* Quick insights */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-sage-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-sage-700">
                {Math.round(results.overall_metrics.sentiment_score * 100)}%
              </div>
              <div className="text-sm text-sage-600">Overall Sentiment</div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-700">
                {Math.round(results.overall_metrics.engagement_level * 100)}%
              </div>
              <div className="text-sm text-blue-600">Engagement Level</div>
            </div>
          </div>

          {/* Key recommendations */}
          {results.recommendations.length > 0 && (
            <div className="text-left mb-6">
              <h3 className="font-semibold text-slate-900 mb-3">Personalized Recommendations:</h3>
              <ul className="space-y-2">
                {results.recommendations.slice(0, 3).map((rec: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-sage-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Take Another Assessment
          </Button>
        </Card>
      </motion.div>
    )
  }

  if (isProcessing) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-blue-600 animate-pulse" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Processing Your Responses
          </h3>
          <p className="text-slate-600">
            Our AI is analyzing your responses and generating personalized insights...
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <span className="text-sm text-slate-500">
            {Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <motion.div
            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Current question */}
      <Card className="p-8 mb-6">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          
          <h2 className="text-xl font-semibold text-slate-900 mb-4">
            Text Assessment
          </h2>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            {currentQuestion}
          </p>
        </div>

        {/* Text input */}
        <div className="mb-6">
          <textarea
            value={currentResponse}
            onChange={(e) => setCurrentResponse(e.target.value)}
            placeholder="Share your thoughts here..."
            className="w-full h-32 p-4 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 resize-none"
            maxLength={1000}
          />
          <div className="text-right text-sm text-slate-500 mt-2">
            {currentResponse.length}/1000 characters
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleSkip}
            className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Skip Question
          </Button>
          
          <Button
            onClick={handleNext}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={!currentResponse.trim()}
          >
            {isLastQuestion ? 'Complete Assessment' : 'Next Question'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </Card>

      {/* Assessment info */}
      <div className="text-center text-sm text-slate-500">
        <div className="flex items-center justify-center space-x-6">
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2 text-blue-500" />
            <span>Take your time</span>
          </div>
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
            <span>Confidential & Secure</span>
          </div>
        </div>
      </div>
    </div>
  )
}